import {
  initialize as createLDClient,
  LDSingleKindContext,
  LDClient,
} from 'launchdarkly-js-client-sdk';

export type LaunchDarklyClient = {
  getFeature(name: string, defaultValue?: boolean): Promise<boolean>;
  getAllFeatures(): Promise<Record<string, boolean>>;
  initialize(userContext: LDSingleKindContext): Promise<void>;
};

function getFeature(client: LDClient, name: string, defaultValue = false) {
  return client.variation(name, defaultValue);
}

function getAllFeatures(client: LDClient) {
  return client.allFlags();
}

export default function createLaunchDarklyClient(
  clientId: string = import.meta.env.LAUNCHDARKLY_CLIENT_ID
): LaunchDarklyClient {
  let storedClient: LDClient;
  let initializedKey: string | undefined;
  const clientQueue: Array<(client: LDClient) => void> = [];

  function storeClient(client: LDClient) {
    storedClient = client;
    while (clientQueue.length > 0) {
      clientQueue.shift()?.(storedClient);
    }
  }

  function getClient() {
    if (storedClient) {
      return storedClient;
    }

    return new Promise<LDClient>(res => {
      clientQueue.push(res);
    });
  }

  async function initialize(userContext: LDSingleKindContext) {
    if (storedClient && initializedKey === userContext.key) {
      return;
    }

    initializedKey = userContext.key as string | undefined;
    const client = createLDClient(clientId, userContext);
    await client.waitUntilReady();
    storeClient(client);
  }

  return {
    async getFeature(name: string, defaultValue?: boolean) {
      return getFeature(await getClient(), name, defaultValue);
    },
    async getAllFeatures() {
      return getAllFeatures(await getClient());
    },
    initialize,
  };
}
